import type { ComponentInit } from "@scripts/registry";

const COVER = "[data-fa-media-player-cover]";
const TRIGGER = "[data-fa-media-player-trigger]";
const VIDEO = "[data-fa-media-player-video]";
const EMBED = "[data-fa-media-player-embed]";
const PLAYING = "fa-media-player--playing";

/* YouTube's protocol. A Vimeo or Brightcove embed reveals and does not start — see the known
   gap in the criteria file's Amendments. */
const PLAY_COMMAND = JSON.stringify({ event: "command", func: "playVideo", args: [] });
const PAUSE_COMMAND = JSON.stringify({ event: "command", func: "pauseVideo", args: [] });

const originOf = (url: string): string | null => {
	try {
		return new URL(url, window.location.href).origin;
	} catch {
		return null;
	}
};

/* Module-level, not per-instance: every player on a page shares this one chunk, so the Set is
   the seam a new play uses to pause whichever ones are already running. */
const playing = new Set<() => void>();

export const initMediaPlayer: ComponentInit = (root) => {
	const cover = root.querySelector<HTMLElement>(COVER);
	const trigger = root.querySelector<HTMLElement>(TRIGGER);

	if (!cover || !trigger) return undefined;

	const video = root.querySelector<HTMLVideoElement>(VIDEO);
	const embed = root.querySelector<HTMLIFrameElement>(EMBED);
	const embedOrigin = embed ? originOf(embed.src) : null;
	const listeners = new AbortController();

	/* Chrome paints the native control bar above the cover, and no repaint is coming. */
	const authoredControls = video?.hasAttribute("controls") ?? false;

	if (authoredControls) video?.removeAttribute("controls");

	/* Without this an iframe behind the cover is still a tab stop, and YouTube's own
	   space-to-play shortcut starts the video with the poster and trigger left on top of it. */
	embed?.setAttribute("tabindex", "-1");

	/* inert as well as invisible: opacity alone would leave the trigger a live tab stop sitting,
	   unseen, on top of whatever is actually playing. */
	const showCover = () => {
		root.classList.remove(PLAYING);
		cover.inert = false;
		embed?.setAttribute("tabindex", "-1");
	};

	const hideCover = () => {
		root.classList.add(PLAYING);
		cover.inert = true;
	};

	/* The video branch needs no explicit showCover() call: pausing it fires the listener below,
	   which is also what catches the viewer's own native controls, not only this. An embed has
	   no such event to listen for, so it is brought back directly instead. */
	const pause = () => {
		playing.delete(pause);
		video?.pause();

		if (embed && embedOrigin) {
			embed.contentWindow?.postMessage(PAUSE_COMMAND, embedOrigin);
			showCover();
		}
	};

	video?.addEventListener("pause", showCover, { signal: listeners.signal });

	trigger.addEventListener(
		"click",
		() => {
			for (const stop of playing) stop();
			playing.add(pause);

			hideCover();

			if (video) {
				if (authoredControls) video.setAttribute("controls", "");

				/* Rejects under an autoplay policy that did not count the click. The cover is
				   already gone, so the native controls are there to press. */
				video.play().catch(() => undefined);
				video.focus();
				return;
			}

			if (!embed) return;

			embed.removeAttribute("tabindex");

			if (embedOrigin) embed.contentWindow?.postMessage(PLAY_COMMAND, embedOrigin);

			embed.focus();
		},
		{ signal: listeners.signal },
	);

	return () => {
		listeners.abort();
		playing.delete(pause);
		showCover();

		if (video) {
			if (authoredControls) video.setAttribute("controls", "");

			video.pause();
			video.currentTime = 0;
		}
	};
};
